import React, { useEffect, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import SkeletonVendorPage from '../LoadingPages/SkeletonVendorPage';
import AgeDisplay from './AgeDisplay';
import { kaReducer, Table } from 'ka-table';
import css from './style.module.css';

import {
  DataType,
  FilteringMode,
  SortingMode,
  PagingPosition,
} from 'ka-table/enums';
import { updatePageSize, search } from 'ka-table/actionCreators';
import { GlobalSearch } from './GlobalSearch';

export default function MarketingComplianceTable({ data, status, tableTitel }) {
  const location = "Marketing Compliance";
  const navigate = useNavigate();
  const [marketingData, setMarketingData] = useState(null);
  const [tableProps, setTableProps] = useState(null);
  const [showSearchFields, setShowSearchFields] = useState(false);

  // Unique identifier for this component's styles
  const COMPONENT_ID = 'marketing-compliance-table';

  // Load marketing data when available.
  useEffect(() => {
    if (data) {
      setMarketingData(data);
    }
  }, [data]);

  // Name cell — display only. Navigation happens at the row level.
  const NameCell = ({ value }) => (
    <span className="u-row-name">{value}</span>
  );

  // Reducer dispatcher
  const dispatch = (action) => {
    setTableProps(prev => kaReducer(prev, action));
  };

  // Handle global search
  const handleGlobalSearch = (value) => {
    dispatch(search(value));
  };

  // Add CSS with unique classes for this component
  useEffect(() => {
    const styleId = `${COMPONENT_ID}-styles`;
    if (document.head && !document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .${COMPONENT_ID} input:focus {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
        }
        
        /* Fix table cell spacing for marketing compliance table */
        .${COMPONENT_ID} .ka-table td, 
        .${COMPONENT_ID} .ka-table th {
          padding: 12px 16px !important;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        /* Responsive spacing for marketing compliance table */
        @media (max-width: 768px) {
          .${COMPONENT_ID} .ka-table td, 
          .${COMPONENT_ID} .ka-table th {
            padding: 8px 12px !important;
            font-size: 14px;
          }
        }
        
        @media (max-width: 576px) {
          .${COMPONENT_ID} .ka-table td, 
          .${COMPONENT_ID} .ka-table th {
            padding: 6px 8px !important;
            font-size: 12px;
          }
        }
        
        .${COMPONENT_ID} .table-responsive-wrapper {
          overflow-x: auto;
          width: 100%;
          border-radius: 8px;
        }
        
        .${COMPONENT_ID} .ka-table thead th {
          background-color: #FFFFFF !important;
          position: sticky;
          top: 0;
          z-index: 10;
          font-weight: 600;
        }
        
        .${COMPONENT_ID} .ka-table tbody tr {
          background-color: var(--surface) !important;
          cursor: pointer;
          transition: background-color 120ms cubic-bezier(0.22, 0.61, 0.36, 1);
        }

        .${COMPONENT_ID} .ka-table tbody tr:hover,
        .${COMPONENT_ID} .ka-table tbody tr:hover td {
          background-color: var(--surface-sunken) !important;
        }

        .${COMPONENT_ID} .ka-table tbody tr:hover .u-row-name {
          color: var(--accent);
        }

        .${COMPONENT_ID} .ka-table tbody tr:hover .u-row-chevron {
          color: var(--accent);
          transform: translateX(2px);
        }

        /* Add column and row lines */
        .${COMPONENT_ID} .ka-table td,
        .${COMPONENT_ID} .ka-table th {
          border-right: 1px solid var(--hairline);
          border-bottom: 1px solid var(--hairline);
        }
        
        /* Remove border from last column */
        .${COMPONENT_ID} .ka-table td:last-child,
        .${COMPONENT_ID} .ka-table th:last-child {
          border-right: none;
        }
        
        /* Remove border from last row */
        .${COMPONENT_ID} .ka-table tbody tr:last-child td {
          border-bottom: none;
        }
      `;
      document.head.appendChild(style);
    }

    // Cleanup function to remove styles when component unmounts
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [COMPONENT_ID]);

  // Configure table props when marketingData updates.
  useEffect(() => {
    if (!marketingData) return;

    const filteredData = marketingData.filter(item => item.status === status);
    const hasApprovedBy = filteredData.some(item => item.approved_by);

    setTableProps({
      columns: [
        {
          key: 'index',
          title: '#',
          dataType: DataType.Number,
          style: {
            textAlign: "center",
            width: "60px",
            padding: "12px 16px",
            minWidth: "60px"
          },
          filteringEnabled: false
        },
        {
          key: 'documentName',
          title: 'Name',
          dataType: DataType.String,
          style: {
            padding: "12px 16px",
            minWidth: "200px",
            maxWidth: "300px"
          }
        },
        {
          key: 'versionNumber',
          title: 'Latest Version',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "120px"
          }
        },
        {
          key: 'created_by',
          title: 'Created By',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "150px"
          }
        },
        ...(tableTitel !== "Awaiting Compliance Review" && hasApprovedBy ? [{
          key: 'approved_by',
          title: 'Reviewed By',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "150px"
          }
        }] : []),
        {
          key: 'status',
          title: 'Status',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "120px"
          }
        },
        {
          key: 'documentType',
          title: 'Document Type',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "150px"
          }
        },
        {
          key: 'mediaChannel',
          title: 'Media Channel',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "150px"
          }
        },
        {
          key: 'targetGeography',
          title: 'Target Geography',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "150px"
          }
        },
        {
          key: 'age',
          title: 'Age',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 16px",
            minWidth: "100px"
          }
        },
        {
          key: 'chevron',
          title: '',
          dataType: DataType.String,
          style: {
            textAlign: "center",
            padding: "12px 8px",
            width: "40px",
            minWidth: "40px"
          },
          filteringEnabled: false
        }
      ],
      data: filteredData.map((item, index) => {
        const latestVersionNum = getLatestVersionNumber(item);
        return {
          ...item,
          index: index + 1,
          documentName: item.documentName || "Unknown Document",
          versionNumber: `V${latestVersionNum}`,
          age: getLatestCreated(item),
          documentType:
            Array.isArray(item.documentType)
              ? item.documentType.join(', ')
              : item.documentType,
          mediaChannel:
            Array.isArray(item.mediaChannel)
              ? item.mediaChannel.join(', ')
              : item.mediaChannel,
          targetGeography:
            Array.isArray(item.targetGeography)
              ? item.targetGeography.join(', ')
              : item.targetGeography,
        };
      }),
      rowKeyField: 'documentId',
      sortingMode: SortingMode.Single,
      filteringMode: showSearchFields ? FilteringMode.FilterRow : FilteringMode.None,
      paging: {
        enabled: true,
        pageIndex: 0,
        pageSize: 20,
        pageSizes: [20, 30, 40],
        position: PagingPosition.Bottom
      },
      childComponents: {
        cellText: {
          content: props => {
            if (props.column.key === 'documentName') {
              return <NameCell value={props.value} />;
            }
            if (props.column.key === 'age') {
              return <AgeDisplay createdAt={props.value} />;
            }
            if (props.column.key === 'chevron') {
              return <i className="fa-solid fa-chevron-right u-row-chevron" aria-hidden="true" />;
            }
            return props.value;
          }
        },
        dataRow: {
          elementAttributes: (props) => ({
            onClick: () => {
              const versionId = getLatestVersionId(props.rowData);
              navigate(`/marketing/details/${versionId}`, {
                state: {
                  data: getLatestVersionData(props.rowData),
                  status: props.rowData.status,
                  firstVersion: props.rowData,
                },
              });
            },
            title: `Open ${props.rowData.documentName}`,
          }),
        },
        table: {
          elementAttributes: () => ({
            className: `${COMPONENT_ID}-table ka-table`,
            style: {
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0',
              border: '1px solid #B3B3B3',
              borderRadius: '8px',
              overflow: 'hidden'
            }
          })
        },
        tableHead: {
          elementAttributes: () => ({
            style: {
              position: 'sticky',
              top: 0,
              backgroundColor: 'var(--surface-sunken)',
              zIndex: 10
            }
          })
        },
        paging: {
          elementAttributes: () => ({
            className: 'custom-paging'
          })
        },
        pagingPages: {
          elementAttributes: () => ({
            className: 'd-flex justify-content-end'
          })
        },
        pagingIndex: {
          elementAttributes: props => ({
            className: `pagination-item ${props.isActive ? 'active' : ''}`
          })
        },
        pagingSizes: {
          content: ({ dispatch: pagingDispatch, pageSize }) => (
            <div className="d-flex align-items-start">
              <span className='text-muted fw-medium' style={{ fontSize: "16px" }}>Rows per page:</span>
              <input
                type="number"
                min={1}
                value={pageSize}
                onChange={e => {
                  const newSize = Number(e.target.value) || 1;
                  pagingDispatch(updatePageSize(newSize))
                }}
                style={{
                  width: '60px',
                  padding: '2px 4px',
                  textAlign: 'center',
                  marginLeft: '8px',
                }}
                title="Rows per page"
              />
            </div>
          ),
          elementAttributes: () => ({
            className: 'ka-paging-size'
          })
        }
      }
    });
  }, [marketingData, status, tableTitel, showSearchFields, COMPONENT_ID]);

  // Loading & empty states
  if (!marketingData) {
    return <SkeletonVendorPage location={location} data={{ tableTitel }} />;
  }
  const filteredData = marketingData.filter(item => item.status === status);
  if (!filteredData.length) {
    return <p className="ms-2 mt-2">No {tableTitel}</p>;
  }
  if (!tableProps) {
    return <SkeletonVendorPage location={location} data={{ tableTitel }} />;
  }

  return (
    <div className={`${COMPONENT_ID} custom-theme-demo`} style={{ width: '100%' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className={css.tableNumber}>{filteredData.length} records</div>
        <GlobalSearch onSearchChange={handleGlobalSearch} />
      </div>
      <div className="table-responsive-wrapper">
        <Table
          {...tableProps}
          dispatch={dispatch}
        />
      </div>
    </div>
  );

  // --- HELPER FUNCTIONS ---

  function getLatestVersionNumber(item) {
    if (Array.isArray(item.version) && item.version.length) {
      return item.version
        .map(v => Number(v.versionNumber) || 0)
        .reduce((max, cur) => Math.max(max, cur), 1)
        .toString();
    }
    return (item.firstVersion || "1").toString();
  }

  function getLatestCreated(item) {
    if (Array.isArray(item.version) && item.version.length) {
      return item.version
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
        .createdAt;
    }
    return item.createdAt;
  }

  function getLatestVersionId(item) {
    if (Array.isArray(item.version) && item.version.length) {
      return item.version
        .reduce((latest, cur) =>
          Number(cur.versionNumber) > Number(latest.versionNumber) ? cur : latest
        ).versionId;
    }
    return item.documentId;
  }

  function getLatestVersionData(item) {
    if (Array.isArray(item.version) && item.version.length) {
      return item.version
        .reduce((latest, cur) =>
          Number(cur.versionNumber) > Number(latest.versionNumber) ? cur : latest
        );
    }
    return item;
  }
}